import { debugLog } from "../debugLog";
import { refreshChoirPlayerApp } from "./refreshApp";

const APP_VERSION_BASE_URL =
  "https://choirplayerugc.s3.amazonaws.com/app-version";

const APP_VERSION_URLS = {
  dev: `${APP_VERSION_BASE_URL}/player-application-dev.json`,
  live: `${APP_VERSION_BASE_URL}/player-application-live.json`,
};

const LOCAL_VERSION_KEY_PREFIX = "choirplayer:remoteAppVersion";
const LAST_CHECK_KEY_PREFIX = "choirplayer:lastRemoteAppVersionCheck";
const UPDATE_PROMPT_DELAY_UNTIL_KEY_PREFIX = "choirplayer:remoteAppVersionPromptDelayUntil";
const UPDATE_PROMPT_DELAY_VERSION_KEY_PREFIX = "choirplayer:remoteAppVersionPromptDelayVersion";
const CHECK_INTERVAL_MS = 3000;
const DEFAULT_UPDATE_DELAY_SECS = 60 * 60;

function logAlways(...args) {
  try {
    console.log(...args);
  } catch (_) {}

  try {
    debugLog(...args);
  } catch (_) {}
}

function getPlayerVersionEnvironment() {
  try {
    const forcedEnvironment = String(
      localStorage.getItem("playerVersionEnvironment") || ""
    ).toLowerCase();

    if (forcedEnvironment === "dev" || forcedEnvironment === "live") {
      return forcedEnvironment;
    }
  } catch (_) {}

  try {
    const hostname = String(window.location.hostname || "").toLowerCase();

    if (
      hostname === "localhost" ||
      hostname === "127.0.0.1" ||
      hostname.startsWith("192.168.") ||
      hostname.startsWith("10.") ||
      hostname.includes("dev")
    ) {
      return "dev";
    }
  } catch (_) {}

  return "live";
}

function getRemoteVersionUrl(environment = getPlayerVersionEnvironment()) {
  return APP_VERSION_URLS[environment] || APP_VERSION_URLS.live;
}

function getEnvironmentKey(prefix, environment = getPlayerVersionEnvironment()) {
  return `${prefix}:${environment}`;
}

function getUpdatePromptDelaySecs() {
  try {
    const configuredValue = Number(localStorage.getItem("delayAppUpdateSecs"));

    if (Number.isFinite(configuredValue) && configuredValue > 0) {
      return configuredValue;
    }
  } catch (_) {}

  return DEFAULT_UPDATE_DELAY_SECS;
}

function getUpdatePromptDelayDisplayText() {
  const delaySecs = Math.max(1, Math.floor(getUpdatePromptDelaySecs()));

  if (delaySecs < 60) {
    return `${delaySecs} second${delaySecs === 1 ? "" : "s"}`;
  }

  const totalMins = Math.floor(delaySecs / 60);

  if (totalMins < 60) {
    return `${totalMins} minute${totalMins === 1 ? "" : "s"}`;
  }

  const hours = Math.floor(totalMins / 60);
  const remainingMins = totalMins % 60;

  if (remainingMins === 0) {
    return `${hours} hour${hours === 1 ? "" : "s"}`;
  }

  return `${hours} hour${hours === 1 ? "" : "s"} ${remainingMins} minute${remainingMins === 1 ? "" : "s"}`;
}

function isUpdatePromptDelayed(remoteVersion, environment) {
  try {
    const delayedVersion = localStorage.getItem(
      getEnvironmentKey(UPDATE_PROMPT_DELAY_VERSION_KEY_PREFIX, environment)
    );
    const delayUntil = Number(
      localStorage.getItem(
        getEnvironmentKey(UPDATE_PROMPT_DELAY_UNTIL_KEY_PREFIX, environment)
      ) || "0"
    );

    return delayedVersion === remoteVersion && Date.now() < delayUntil;
  } catch (_) {
    return false;
  }
}

function delayUpdatePrompt(remoteVersion, environment) {
  const delaySecs = getUpdatePromptDelaySecs();
  const delayText = getUpdatePromptDelayDisplayText();
  const delayUntil = Date.now() + delaySecs * 1000;

  try {
    localStorage.setItem(
      getEnvironmentKey(UPDATE_PROMPT_DELAY_VERSION_KEY_PREFIX, environment),
      remoteVersion
    );
    localStorage.setItem(
      getEnvironmentKey(UPDATE_PROMPT_DELAY_UNTIL_KEY_PREFIX, environment),
      String(delayUntil)
    );
  } catch (_) {}

  logAlways("[APP VERSION] update prompt delayed", {
    environment,
    remoteVersion,
    delaySecs,
    delayText,
    delayUntil: new Date(delayUntil).toISOString(),
  });

}

function clearUpdatePromptDelay(environment) {
  try {
    localStorage.removeItem(
      getEnvironmentKey(UPDATE_PROMPT_DELAY_VERSION_KEY_PREFIX, environment)
    );
    localStorage.removeItem(
      getEnvironmentKey(UPDATE_PROMPT_DELAY_UNTIL_KEY_PREFIX, environment)
    );
  } catch (_) {}
}

function askUserToUpdateApp(remoteVersion, environment) {
  return new Promise((resolve) => {
    try {
      const delayText = getUpdatePromptDelayDisplayText();

      const overlay = document.createElement("div");
      overlay.setAttribute("role", "dialog");
      overlay.setAttribute("aria-modal", "true");
      overlay.style.position = "fixed";
      overlay.style.zIndex = "2147483647";
      overlay.style.top = "0";
      overlay.style.left = "0";
      overlay.style.right = "0";
      overlay.style.bottom = "0";
      overlay.style.background = "rgba(0, 0, 0, 0.45)";
      overlay.style.display = "flex";
      overlay.style.alignItems = "center";
      overlay.style.justifyContent = "center";
      overlay.style.padding = "20px";

      const panel = document.createElement("div");
      panel.style.maxWidth = "500px";
      panel.style.width = "100%";
      panel.style.background = "#fff";
      panel.style.color = "#222";
      panel.style.borderRadius = "8px";
      panel.style.boxShadow = "0 10px 30px rgba(0, 0, 0, 0.35)";
      panel.style.padding = "22px";
      panel.style.fontFamily = "Arial, sans-serif";

      const title = document.createElement("div");
      title.textContent = "Update app";
      title.style.fontSize = "20px";
      title.style.fontWeight = "bold";
      title.style.marginBottom = "12px";

      const message = document.createElement("div");
      message.textContent =
        "A new version of Choir Player is available.";
      message.style.fontSize = "15px";
      message.style.lineHeight = "1.4";
      message.style.marginBottom = "20px";

      const detail = document.createElement("div");
      detail.textContent = `Version: ${remoteVersion} (${environment})`;
      detail.style.fontSize = "12px";
      detail.style.color = "#666";
      detail.style.marginBottom = "18px";

      const buttonRow = document.createElement("div");
      buttonRow.style.display = "flex";
      buttonRow.style.justifyContent = "flex-end";
      buttonRow.style.gap = "10px";
      buttonRow.style.flexWrap = "wrap";

      const noButton = document.createElement("button");
      noButton.type = "button";
      noButton.textContent = `No. Check again after ${delayText}`;

      noButton.style.padding = "10px 14px";
      noButton.style.borderRadius = "5px";
      noButton.style.border = "1px solid #aaa";
      noButton.style.background = "#fff";
      noButton.style.cursor = "pointer";

      const okButton = document.createElement("button");
      okButton.type = "button";
      okButton.textContent = "OK";
      okButton.style.padding = "10px 16px";
      okButton.style.borderRadius = "5px";
      okButton.style.border = "1px solid #222";
      okButton.style.background = "#222";
      okButton.style.color = "#fff";
      okButton.style.cursor = "pointer";

      const finish = (shouldUpdate) => {
        try {
          document.body.removeChild(overlay);
        } catch (_) {}
        resolve(shouldUpdate);
      };

      noButton.addEventListener("click", () => finish(false));
      okButton.addEventListener("click", () => finish(true));

      buttonRow.appendChild(noButton);
      buttonRow.appendChild(okButton);
      panel.appendChild(title);
      panel.appendChild(message);
      panel.appendChild(detail);
      panel.appendChild(buttonRow);
      overlay.appendChild(panel);
      document.body.appendChild(overlay);

      okButton.focus();
    } catch (e) {
      logAlways("[APP VERSION] custom update dialog failed", e);
      resolve(false);
    }
  });
}

async function activateWaitingServiceWorkerIfPresent() {
  if (!("serviceWorker" in navigator)) return false;

  const registrations = await navigator.serviceWorker.getRegistrations();

  for (const registration of registrations) {
    if (!registration.waiting) continue;

    logAlways("[APP VERSION] activating waiting service worker", registration.scope);

    await new Promise((resolve) => {
      let resolved = false;

      const finish = () => {
        if (resolved) return;
        resolved = true;
        resolve(true);
      };

      const waiting = registration.waiting;

      waiting.addEventListener("statechange", (event) => {
        if (event.target.state === "activated") {
          finish();
        }
      });

      waiting.postMessage({ type: "SKIP_WAITING" });

      setTimeout(finish, 3000);
    });

    return true;
  }

  return false;
}

export async function forceChoirPlayerAppRefresh(reason = "remote-version-change") {
  logAlways("[APP VERSION] force refresh start", reason);

  try {
    const activatedWaitingServiceWorker =
      await activateWaitingServiceWorkerIfPresent();

    if (activatedWaitingServiceWorker) {
      logAlways("[APP VERSION] waiting service worker activated");
      window.location.reload();
      return;
    }
  } catch (e) {
    logAlways("[APP VERSION] waiting service worker activation failed", e);
  }

  try {
    if (typeof caches !== "undefined" && caches?.keys) {
      const cacheNames = await caches.keys();

      for (const name of cacheNames) {
        logAlways("[APP VERSION] deleting cache", name);
        await caches.delete(name);
      }
    }
  } catch (e) {
    logAlways("[APP VERSION] cache delete failed", e);
  }

  try {
    if (navigator?.serviceWorker?.getRegistrations) {
      const registrations = await navigator.serviceWorker.getRegistrations();

      for (const registration of registrations) {
        logAlways("[APP VERSION] unregistering service worker", registration.scope);
        await registration.unregister();
      }
    }
  } catch (e) {
    logAlways("[APP VERSION] service worker unregister failed", e);
  }

  window.location.reload();
}

export async function checkRemoteAppVersion(source = "unknown") {
  try {
    const environment = getPlayerVersionEnvironment();
    const versionUrl = getRemoteVersionUrl(environment);
    const lastCheckKey = getEnvironmentKey(LAST_CHECK_KEY_PREFIX, environment);
    const localVersionKey = getEnvironmentKey(LOCAL_VERSION_KEY_PREFIX, environment);
    const now = Date.now();
    const lastCheck = Number(localStorage.getItem(lastCheckKey) || "0");

    if (now - lastCheck < CHECK_INTERVAL_MS) {
      return false;
    }

    localStorage.setItem(lastCheckKey, String(now));

    const url = `${versionUrl}?t=${Date.now()}`;

    logAlways("[APP VERSION] checking remote version", {
      source,
      environment,
      url,
    });

    const response = await fetch(url, {
      cache: "no-store",
      credentials: "omit",
    });

    if (!response.ok) {
      logAlways("[APP VERSION] version check failed status", {
        environment,
        status: response.status,
      });
      return false;
    }

    const json = await response.json();
    const remoteVersion = String(json.version || "").trim();

    if (!remoteVersion) {
      logAlways("[APP VERSION] remote version missing", { environment });
      return false;
    }

    const localVersion = localStorage.getItem(localVersionKey);

    logAlways("[APP VERSION] version values", {
      source,
      environment,
      localVersion,
      remoteVersion,
    });

    if (!localVersion) {
      localStorage.setItem(localVersionKey, remoteVersion);
      logAlways("[APP VERSION] stored initial version", {
        environment,
        remoteVersion,
      });
      return false;
    }

    if (localVersion === remoteVersion) {
      clearUpdatePromptDelay(environment);
      return false;
    }

    logAlways("[APP VERSION] new version detected", {
      environment,
      localVersion,
      remoteVersion,
    });

    if (isUpdatePromptDelayed(remoteVersion, environment)) {
      logAlways("[APP VERSION] update prompt currently delayed", {
        environment,
        remoteVersion,
        delayUntil: localStorage.getItem(
          getEnvironmentKey(UPDATE_PROMPT_DELAY_UNTIL_KEY_PREFIX, environment)
        ),
      });
      return false;
    }

    const shouldUpdate = await askUserToUpdateApp(remoteVersion, environment);

    if (!shouldUpdate) {
      delayUpdatePrompt(remoteVersion, environment);
      return false;
    }

    clearUpdatePromptDelay(environment);

    // Store before reload to avoid an update prompt loop if the refresh succeeds.
    localStorage.setItem(localVersionKey, remoteVersion);

    await refreshChoirPlayerApp({
      source: "remote-version-change",
      confirmIfOffline: true,
    });

    return true;
  } catch (e) {
    logAlways("[APP VERSION] check failed", e);
    return false;
  }
}

try {
  window.__CP_CHECK_REMOTE_VERSION__ = checkRemoteAppVersion;
  window.__CP_FORCE_APP_REFRESH__ = forceChoirPlayerAppRefresh;
  window.__CP_GET_VERSION_ENVIRONMENT__ = getPlayerVersionEnvironment;
  window.__CP_GET_VERSION_URL__ = () => getRemoteVersionUrl(getPlayerVersionEnvironment());
} catch (_) {}
